import React, { FC, useEffect, useState, useCallback } from "react";

import { DatePicker, Radio, Tabs } from "antd";
import moment from "moment";
import { DownOutlined } from "@ant-design/icons";

import "./SchoolLoginDetails.scss";

import InstituteService from "../../../services/InstituteService/institute.service";
import { Institutes } from "../../../models/InstituteModel/institutes.model";
import { MetaService } from "../../../services/MetaService/meta.service";
import { MetaModel } from "../../../models/CourseModels/SubjectModel/subject.model";
import { StudentActivitiesFilterModel } from "../../../models/DashboardModel/DashboardFilterModel/dashboardFilter.model";
import { dateFilterFunc } from "../../../shared/utils/filter";

import LoginDetailCard from "./LoginDetailCard";
import InstituteFilter from "../InstituteFilter";
import { useStudentActivities } from "../../../hooks/Institute/useStudentActivities";
import { LoadingComponent } from "../../../shared/components/LoadingComponent";

const initialFilterState: StudentActivitiesFilterModel = {
  branch: undefined,
  startingDate: "0",
  endingDate: "0",
};

const SchoolLoginDetails: FC = () => {
  const [institutes, setInstitutes] = useState<Institutes[]>([]);
  const [branches, setBranches] = useState<MetaModel[]>([]);
  const [classified, setClassified] = useState<string>("");
  const [filters, setFilters] =
    useState<StudentActivitiesFilterModel>(initialFilterState);
  const [loading, setLoading] = useState(false);

  const { getBranchLogins, branchLoginsData } = useStudentActivities();

  const fetchBranches = useCallback((instituteId: number) => {
    setLoading(true);
    MetaService.fetchInstituteBranchesMeta(
      instituteId,
      (data) => {
        setBranches(data);
        setLoading(false);
      },
      () => {},
      () => {}
    );
  }, []);

  const fetchInstitutes = useCallback(() => {
    setLoading(true);
    InstituteService.fetchInstitutes(
      {},
      (data) => {
        setInstitutes(data);
        if (data[0]) fetchBranches(data[0].id);
        setLoading(false);
      },
      () => setLoading(false),
      () => {}
    );
  }, [fetchBranches]);

  useEffect(() => {
    fetchInstitutes();
  }, [fetchInstitutes]);

  const handleDateFilterChange = (value: string) => {
    setClassified(value);

    if (value !== "month") {
      const dateRange = dateFilterFunc(value);
      setFilters((prev) => ({ ...prev, ...dateRange }));
    }
  };

  const handleMonthChange = (date: moment.Moment | null) => {
    if (date) {
      const monthRange = dateFilterFunc("month", date);
      setFilters((prev) => ({ ...prev, ...monthRange }));
    }
  };

  const handleInstituteChange = (instituteId: string) => {
    fetchBranches(Number(instituteId));
    setClassified("");
    setFilters(initialFilterState);
  };

  const handleFilterApply = (values: Partial<StudentActivitiesFilterModel>) => {
    setFilters((prev) => ({ ...prev, ...values }));
    setClassified("");
  };

  const handleFilterClear = () => {
    setFilters(initialFilterState);
    setClassified("");
  };

  useEffect(() => {
    getBranchLogins(filters);
  }, [filters]);

  const BranchCards: FC<{}> = () => (
    <div className="school-cards">
      {loading ? <LoadingComponent title="Loading data..."/> : branches.map((branch) => {
        const label = branch.label ?? "";
        let branchData = {
          total: branchLoginsData.active_count[label] || 0,
          logins: branchLoginsData.logins[label] || 0,
          notLogins: branchLoginsData.not_logins[label] || 0,
        };
        return (
          <LoginDetailCard
            key={branch.value}
            branch={branch}
            additionalFilters={filters}
            data={branchData}
          />
        );
      })}
    </div>
  );
  return (
    <div className="school-login-details">
      <div className="institute-activities__header">
        <div className="institute-activities__header-title">
          Institute Activities
          <InstituteFilter
            filterFor="InstituteActivities"
            applyHandler={handleFilterApply}
            clearHandler={handleFilterClear}
          />
        </div>
        <div className="institute-activities__header-classifieds">
          <Radio.Group
            onChange={(e) => handleDateFilterChange(e.target.value)}
            value={classified}
          >
            <Radio.Button value="today">Today</Radio.Button>
            <Radio.Button value="yesterday">Yesterday</Radio.Button>
            <Radio.Button value="week">Week</Radio.Button>
            <Radio.Button value="month">Month</Radio.Button>
          </Radio.Group>
          {classified === "month" && (
            <DatePicker
              picker="month"
              format="MMM YYYY"
              suffixIcon={<DownOutlined />}
              onChange={handleMonthChange}
            />
          )}
        </div>
      </div>

      <Tabs
        onTabClick={handleInstituteChange}
        items={institutes.map((ins) => ({
          key: String(ins.id),
          label: (
            <span>
              <img
                src={ins.logoUrl}
                alt=""
                style={{ width: 16, height: 16, marginRight: 8 }}
              />
              {ins.name}
            </span>
          ),
          children: <BranchCards />,
        }))}
    />
    </div>
  );
};

export default SchoolLoginDetails;
